import { Portal } from "@c/Portal";
import { useNotebookStore } from "@store/index";
import React, { useCallback, useLayoutEffect, useState } from "react";
import { RenderElementProps, RenderLeafProps } from "slate-react";
import { useInteraction } from "./interactions";
import { IInternalLink, isBlock } from "./types";
import { useIsActive } from "./useIsActive";
import { useWindowLayout } from "./useWindowLayout";

export const Title: React.FC<RenderElementProps> = (props) => {
  return <h1 {...props.attributes}>{props.children}</h1>;
};

export const Code: React.FC<RenderElementProps> = (props) => {
  return (
    <pre {...props.attributes}>
      <code>{props.children}</code>
    </pre>
  );
};

export const useCoordinates = (ref: any) => {
  const [offsets, setOffets] = useState<null | DOMRect>(null);
  const { scrolls, sizes } = useWindowLayout();

  useLayoutEffect(() => {
    const el = ref?.current;

    if (el) {
      setOffets(el.getBoundingClientRect() as DOMRect);
    }
  }, [ref, sizes, scrolls]);

  const position = offsets
    ? {
      left: offsets.left + scrolls.x,
      right: offsets.right + scrolls.x,
      top: offsets.top + scrolls.y,
      bottom: offsets.bottom + scrolls.y,
    }
    : null;

  return { offsets, scrolls, position };
};

export const Block: React.FC<RenderElementProps> = (props) => {
  const element = props.element;
  const i = useInteraction();

  // TODO: reword slate type system to use a generic instead.
  if (!isBlock(element)) {
    throw new Error("invalid element");
  }

  const { ref } = props.attributes;

  const { position } = useCoordinates(ref);

  const onClick: React.MouseEventHandler = useCallback(
    (event) => {
      return i.onClickInternalLink(event, element);
    },
    [i, element]
  );

  const notebookStore = useNotebookStore();
  const url = notebookStore.resolve(element.subject);
  const isActive = useIsActive();

  return (
    <>
      <div className={`block ${element.hasChanges ? 'needs-refresh' : ''}`} {...props.attributes}>
        {props.children}
      </div>
      <Portal>
        {position && element.subject && (
          <div className={isActive ? "is-active" : "is-inactive"}>
            <div
              className={`block-subject`}
              style={{
                left: position.left + 12,
                top: position.top - 10,
              }}
            >
              <a
                href={url}
                onClick={isActive ? (e) => e.preventDefault() : onClick}
              >
                {element.subject}
              </a>
            </div>
          </div>
        )}
      </Portal>
    </>
  );
};

export const InternalLink: React.FC<RenderElementProps> = (props) => {
  const e = props.element as IInternalLink;
  const { ref } = props.attributes;

  const i = useInteraction();
  const notebookStore = useNotebookStore();
  const url = notebookStore.resolve(e.target);

  const onClick: React.MouseEventHandler = useCallback(
    (event) => {
      return i.onClickInternalLink(event, e);
    },
    [i, e]
  );

  const { position, offsets } = useCoordinates(ref);

  return (
    <>
      <a {...props.attributes} href={url} onClick={(e) => e.preventDefault()}>
        {props.children}
      </a>
      <Portal>
        {e.focused && offsets && position && url && (
          <div
            className="internallink-open"
            style={{
              position: "absolute",
              left: position.left,
              top: position.top - 30,
              width: offsets.width,
              transform: `translateY(-50%)`,
              zIndex: 10000,
            }}
          >
            <a className="button" href={url} onClick={onClick}>
              <span>{"Open"}</span>
            </a>
          </div>
        )}
      </Portal>
    </>
  );
};

export const Paragraph: React.FC<RenderElementProps> = (props) => {
  return <p {...props.attributes}>{props.children}</p>;
};

export const Leaf: React.FC<RenderLeafProps> = (props) => {
  return (
    <span
      {...props.attributes}
      style={{ fontWeight: props.leaf.bold ? "bold" : "normal" }}
    >
      {props.children}
    </span>
  );
};
